import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import refundNotImage from '@/assets/refund/refundNot.png';

import FlexWrap from '@/components/common/FlexWrap';
import HospitalsFilter from '@/components/common/HospitalsFilter';
import Paging from '@/components/common/Paging';

import ViewSelector from '@/components/common/ViewSelector';
import DateSelector from '@/components/common/DateSelector';
import { useAppSelector } from '@/hooks/useReduxHooks';
import { IRefundDetailContent } from '@/types/refund';
import LoadingView from '@/components/common/LoadingView';

import { useEffect, useState } from 'react';
import useGetGlobalStatus from '@/hooks/useGetGlobalStatus';
import SearchBar from '../refundDetail/SearchBar';
import DiscountDetailItem from './DiscountDetailItem';
import { DiscountGraphHeader } from '@/constants/discount.const';
import TableColumnFilter from './TableColumnFilter';
import TableHeader from './TableHeader';
import { useRefundDetailForDiscount } from '@/hooks/useRefundDetailForDiscount';

function DiscountDetailList() {
  const {
    data,
    totalPage,
    refetch,
    pageNumber,
    setPageNumber,
    setSize,
    isLoading,
  } = useRefundDetailForDiscount();
  const { globalStatus } = useGetGlobalStatus();
  const { hospitals } = useAppSelector((state) => state.user);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const methods = useForm({
    defaultValues: {
      checked: false,
    },
  });

  useEffect(() => {
    const showColumnValue = localStorage.getItem('showColumn');
    if (showColumnValue) {
      setSelectedFilters(JSON.parse(showColumnValue));
    } else {
      setSelectedFilters(DiscountGraphHeader.map(({ title }) => title));
    }
  }, [globalStatus]);

  return (
    <FormProvider {...methods}>
      <Header>
        <SubTitle>할인 내역</SubTitle>
        <FlexWrap gap='10px' alignItems='center'>
          <ViewSelector setPageNumber={setPageNumber} setSize={setSize} />
          {hospitals.length > 0 && (
            <HospitalsFilter setPageNumber={setPageNumber} />
          )}
          <DateSelector isRefundDetail setPageNumber={setPageNumber} />
        </FlexWrap>
      </Header>
      <SortingWrapper>
        <Filter>
          <TableColumnFilter
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
          />
        </Filter>
        <SearchBar />
      </SortingWrapper>
      {isLoading ? (
        <LoadingView height='80vh' />
      ) : (
        <>
          <TableHeader
            titleList={DiscountGraphHeader}
            selectedFilters={selectedFilters}
          />
          {data && data.content.length > 0 ? (
            <List>
              {data.content.map(
                (refund: IRefundDetailContent, index: number) => (
                  <DiscountDetailItem
                    key={index}
                    refund={refund}
                    index={index}
                    currentPage={data.number}
                    refetch={refetch}
                    showKioskPrice={false}
                    selectedFilters={selectedFilters}
                  />
                ),
              )}
            </List>
          ) : (
            <RefundNotContainer>
              <Image src={refundNotImage} />
              <Text>등록 내역이 없습니다.</Text>
            </RefundNotContainer>
          )}
        </>
      )}

      <Paging
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        totalPage={totalPage}
      />
    </FormProvider>
  );
}

export default DiscountDetailList;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 28px;
`;

const SubTitle = styled.h3`
  font-size: ${(props) => props.theme.fontSize.regular};
  font-weight: ${(props) => props.theme.fontWeight.bold};
  line-height: ${(props) => props.theme.lineHeight.regular};
  color: ${(props) => props.theme.mono[80]};
`;

const List = styled.ul`
  margin-bottom: 30px;
`;

const RefundNotContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  flex: 1;
`;

const Image = styled.img`
  width: 120px;
  height: 120px;
`;

const Text = styled.span`
  font-size: ${(props) => props.theme.fontSize.regular};
  font-weight: ${(props) => props.theme.fontWeight.medium};
  line-height: ${(props) => props.theme.lineHeight.regular};
  color: ${(props) => props.theme.mono[50]};
`;

const SortingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
`;

const Filter = styled.div`
  display: flex;
  gap: 24px;
`;
